import { Box, Divider, Menu, MenuItem, Typography } from "@mui/material";
import { useState } from "react";
import useAPI from "../../../api/hooks/useAPI";
import useColors from "../../../api/hooks/useColors";
import CustomTooltip from "../../../components/CustomTooltip";
import SvgToIcon from "../../../icons/SvgToIcon";
import { TimePoint } from "../../../utils/constants";
import { icons } from "../../stack/environment/icons/iconExport";
import { timePointOptions } from "./utils/commonWidgetUtils";

const TrendSelector = ({ baseTimePoint, setBaseTimePoint }) => {
  const colors = useColors();
  const { trackEvent } = useAPI();
  const [anchorEl, setAnchorEl] = useState(null);
  const handleClickFilterMenu = (event) => {
    trackEvent("User clicked on trend selector");
    setAnchorEl(event.currentTarget);
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexDirection: "row",
        }}
      >
        {baseTimePoint !== TimePoint.LATEST && (
          <Box
            onClick={() => setBaseTimePoint(TimePoint.LATEST)}
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              color: colors.primary_300,
              cursor: "pointer",
              height: "32px",
            }}
          >
            <SvgToIcon
              IconLogo={icons.UndoIcon}
              style={{ width: "18px", height: "18px", marginRight: "4px" }}
            />
            <Typography variant="overline">Back to Current</Typography>
          </Box>
        )}
        <CustomTooltip title="View changes since selected time" placement="top">
          <Box
            onClick={handleClickFilterMenu}
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              padding: "8px",
              background: colors.common_white,
              color: colors.action_active,
              height: "32px",
              border: "1px solid " + colors.enabled_stroke,
              borderRadius: "8px",
              textTransform: "none",
              whiteSpace: "nowrap",
              "&:hover": {
                background: colors.grey_8percent,
              },
            }}
          >
            <Typography variant="overline">
              {baseTimePoint === TimePoint.LATEST
                ? `View trend from`
                : timePointOptions.find(
                    (option) => option.key === baseTimePoint
                  )?.label}
            </Typography>
            <SvgToIcon
              IconLogo={icons.chevronDownIcon}
              style={{ width: "18px" }}
            />
          </Box>
        </CustomTooltip>
      </Box>
      <Menu
        id="trend-selector"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{
          "& .MuiMenu-list": {
            padding: "4px 0",
          },
        }}
        PaperProps={{
          style: {
            borderRadius: "8px",
            padding: "4px 8px",
            minWidth: "160px",
            boxShadow: "0px 2px 8px 0px " + colors.grey_48percent,
          },
        }}
      >
        <Box
          sx={{ display: "flex", flexDirection: "column", marginBottom: "4px" }}
        >
          <Typography
            variant="caption"
            sx={{
              padding: "8px 4px",
              color: colors.text_secondary,
            }}
          >
            View trend from
          </Typography>
          <Divider />
        </Box>
        {timePointOptions.map((option) => (
          <MenuItem
            sx={{
              color: colors.text_primary,
              borderRadius: "6px",
              height: "36px",
              minHeight: "36px",
              padding: "4px",
              "&.Mui-selected": {
                backgroundColor:
                  baseTimePoint === option.key
                    ? colors.grey_8percent
                    : "transparent",
                "&:hover": {
                  backgroundColor: colors.grey_4percent,
                },
              },
              "&:hover": {
                backgroundColor: colors.grey_4percent,
              },
            }}
            key={option.key}
            onClick={() => {
              trackEvent(
                "User clicked on trend selector option: " + option.label
              );
              setBaseTimePoint(option.key);
            }}
            selected={baseTimePoint === option.key}
          >
            <Box
              key={option.key}
              style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant={baseTimePoint === option.key ? "overline" : "caption"}
                sx={{
                  color: colors.text_secondary,
                }}
              >
                {option.label}
              </Typography>
              {baseTimePoint === option.key && (
                <SvgToIcon
                  IconLogo={icons.DoneIcon}
                  style={{ width: "18px" }}
                />
              )}
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default TrendSelector;
